<template>
  <b-container fluid id="card-payment-page">
    <div
      v-if="showLoading"
      class="
        vh-100
        d-flex
        flex-column
        justify-content-center
        align-items-center
      "
    >
      <div class="loader"></div>
      <div class="h3 mt-4">Processing Payment...</div>
      <div>Please wait while we confirm your payment</div>
    </div>
    <template v-else>
      <template v-if="showCardAuthentication">
        <iframe
          :src="card3dsAuthUrl"
          title="Card Authentication"
          class="card-auth-iframe"
        >
        </iframe>
        <img
          @click="showCardAuthentication = false"
          src="@/assets/images/close.svg"
          class="card-auth-close-button"
          style=""
        />
      </template>
      <b-row v-else>
        <b-col md="6" id="order-summary-section" v-if="orderDetails">
          <b-row>
            <b-col md="10" class="mx-auto">
              <div class="h2 mb-3">Order Summary</div>
              <img
                src="@/assets/images/credit-card-visa-master.png"
                alt="Credit Card"
              />
              <span class="h5 ml-3">Credit Card</span>
            </b-col>
          </b-row>
          <!-- VISIBLE ON EXTRA SMALL TO SMALL SCREEN START -->
          <div class="d-md-none">
            <b-row class="my-3">
              <b-col>
                <b-row class="pb-1">
                  <b-col class="font-weight-bold">Total Amount </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <span class="h4"
                      >&#8369;
                      {{ orderDetails ? orderDetails.computed_total : 0 | currency }}</span
                    >
                  </b-col>
                  <b-col class="position-relative">
                    <div
                      v-b-toggle.collapase-order-summary
                      class="chevron-toggle"
                    >
                      <img
                        src="@/assets/images/chevron-up.png"
                        alt="Chevron Up"
                        class="chevron-icon when-opened"
                      />
                      <img
                        src="@/assets/images/chevron-down.png"
                        alt="Chevron Down"
                        class="chevron-icon when-closed"
                      />
                    </div>
                  </b-col>
                </b-row>
                <b-collapse id="collapase-order-summary" class="mt-3">
                  <hr />
                  <div
                    v-for="(order, index) in orderDetails.orders"
                    :key="index"
                  >
                    <div class="d-flex justify-content-between">
                      <div
                        class="font-weight-bold text-truncate collapse-branch"
                      >
                        {{ order.brand_name }}
                      </div>
                      <div>{{ order.order_items.length }} item(s)</div>
                      <div class="font-weight-bold">
                        &#8369;{{ order.order_amount | currency }}
                      </div>
                    </div>
                    <hr />
                  </div>
                  <div
                    v-if="
                      orderDetails.voucher_code && orderDetails.voucher_amount
                    "
                  >
                    <div class="d-flex justify-content-between">
                      <div class="font-weight-bold text-truncate">
                        Discount
                        <div>({{ orderDetails.voucher_code }})</div>
                      </div>
                      <div class="font-weight-bold">
                        - &#8369;{{ orderDetails.voucher_amount | currency }}
                      </div>
                    </div>
                    <hr />
                  </div>
                  <template v-if="orderDetails.fees.length > 0">
                    <div
                      v-for="(fee, feeIndex) in orderDetails.fees"
                      :key="`fee_${feeIndex}`"
                    >
                      <div class="d-flex justify-content-between">
                        <div
                          class="font-weight-bold text-truncate collapse-branch text-capitalize"
                        >
                          {{ fee.name.replace(/_/g, " ") }}
                        </div>
                        <div></div>
                        <div class="font-weight-bold">
                          &#8369;{{ fee.calculated_amount | currency }}
                        </div>
                      </div>
                    </div>
                  </template>
                </b-collapse>
              </b-col>
            </b-row>
          </div>
          <!-- VISIBLE ON EXTRA SMALL TO SMALL SCREEN -->
          <!-- VISIBLE ON MEDIUM AND ABOVE SCREEN START -->
          <div class="d-none d-md-block">
            <b-row>
              <b-col md="10" class="mx-auto">
                <hr />
              </b-col>
            </b-row>
            <div v-for="(order, index) in orderDetails.orders" :key="index">
              <b-row>
                <b-col md="10" class="mx-auto">
                  <b-row>
                    <b-col class="font-weight-bold">
                      {{ order.brand_name }}
                    </b-col>
                    <b-col> {{ order.order_items.length }} item(s) </b-col>
                    <b-col class="font-weight-bold text-right">
                      &#8369;{{ order.order_amount | currency }}
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="10" class="mx-auto">
                  <hr />
                </b-col>
              </b-row>
            </div>
            <div
              v-if="orderDetails.voucher_code && orderDetails.voucher_amount"
            >
              <b-row>
                <b-col md="10" class="mx-auto">
                  <b-row>
                    <b-col class="font-weight-bold">
                      Discount ({{ orderDetails.voucher_code }})
                    </b-col>
                    <b-col></b-col>
                    <b-col class="font-weight-bold text-right">
                      - &#8369;{{ orderDetails.voucher_amount | currency }}
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="10" class="mx-auto">
                  <hr />
                </b-col>
              </b-row>
            </div>
            <template v-if="orderDetails.fees.length > 0">
              <b-row
                v-for="(fee, feeIndex) in orderDetails.fees"
                :key="`fee_${feeIndex}`"
              >
                <b-col md="10" class="mx-auto">
                  <b-row>
                    <b-col class="font-weight-bold text-capitalize">
                      {{ fee.name.replace(/_/g, " ") }}
                    </b-col>
                    <b-col> </b-col>
                    <b-col class="font-weight-bold text-right">
                      &#8369;{{ fee.calculated_amount | currency }}
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="10" class="mx-auto">
                  <hr />
                </b-col>
              </b-row>
            </template>
            <b-row>
              <b-col md="11" class="text-right h5"> Total Amount </b-col>
            </b-row>
            <b-row>
              <b-col md="11" class="text-right h2">
                PHP {{ orderDetails.computed_total | currency }}
              </b-col>
            </b-row>
          </div>
          <!-- VISIBLE ON MEDIUM AND ABOVE SCREEN END -->
        </b-col>
        <b-col md="6" id="card-details-section">
          <b-row id="card-details-form">
            <b-col md="10" class="mx-auto">
              <b-row class="mt-md-5">
                <b-col class="h5"> Card Details </b-col>
                <b-col class="text-right">
                  <img
                    src="@/assets/images/credit-card-master.png"
                    alt="Master Card"
                    class="border rounded py-1 px-2 mx-1"
                  />
                  <img
                    src="@/assets/images/credit-card-visa.png"
                    alt="Visa Card"
                    class="border rounded py-2 px-3 mx-1"
                  />
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col>
                  <div class="floating-field-container">
                    <div class="floating-field">
                      <input
                        type="text"
                        v-model="form.firstName"
                        placeholder=" "
                      />
                      <label>First Name</label>
                    </div>
                  </div>
                  <b-form-invalid-feedback :state="validate('firstName')">
                    {{ displayError("firstName") }}
                  </b-form-invalid-feedback>
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col>
                  <div class="floating-field-container">
                    <div class="floating-field">
                      <input
                        type="text"
                        v-model="form.lastName"
                        placeholder=" "
                      />
                      <label>Last Name</label>
                    </div>
                  </div>
                  <b-form-invalid-feedback :state="validate('lastName')">
                    {{ displayError("lastName") }}
                  </b-form-invalid-feedback>
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col>
                  <div class="floating-field-container">
                    <div class="floating-field">
                      <input
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        v-model="form.cardNumber"
                        placeholder=" "
                        maxlength="16"
                      />
                      <label>Card Number</label>
                    </div>
                  </div>
                  <b-form-invalid-feedback :state="validate('cardNumber')">
                    {{ displayError("cardNumber") }}
                  </b-form-invalid-feedback>
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col>
                  <div class="floating-field-container">
                    <div class="floating-field">
                      <input
                        type="text"
                        v-model="form.expiryDate"
                        placeholder=" "
                        maxlength="5"
                        @keyup="formatExpiryDate"
                        @keydown="formatExpiryDate"
                      />
                      <label>Expiry Date (MM/YY)</label>
                    </div>
                  </div>
                  <b-form-invalid-feedback :state="validate('expiryDate')">
                    {{ displayError("expiryDate") }}
                  </b-form-invalid-feedback>
                </b-col>
                <b-col>
                  <div class="floating-field-container">
                    <div class="floating-field">
                      <input
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        v-model="form.cvv"
                        placeholder=" "
                        maxlength="3"
                        onpaste="return false;"
                      />
                      <label>CVV</label>
                    </div>
                  </div>
                  <b-form-invalid-feedback :state="validate('cvv')">
                    {{ displayError("cvv") }}
                  </b-form-invalid-feedback>
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col>
                  <b-form-invalid-feedback :state="validate('errors')">
                    {{ displayError("errors") }}
                  </b-form-invalid-feedback>
                </b-col>
              </b-row>
              <b-row class="mt-4">
                <b-col md="10" class="mx-auto">
                  <b-button
                    class="
                      btn-core
                      border-0
                      d-inline-flex
                      align-items-center
                      justify-content-center
                    "
                    @click="onCompletePayment"
                    :style="{
                      backgroundColor: `${design.main_color} !important`,
                      color: `${design.button_text_color} !important`
                    }"
                    block
                    :disabled="loading"
                  >
                    {{
                      `${
                        loading
                          ? "Proceeding... Please wait."
                          : "COMPLETE PAYMENT"
                      }`
                    }}
                    <span
                      class="spinner d-inline-block ml-2"
                      v-if="loading"
                    ></span>
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row id="card-details-footer">
            <b-container class="px-4">
              <b-row class="mt-3">
                <b-col class="d-flex justify-content-between">
                  <div>
                    <img
                      src="@/assets/images/lock.png"
                      id="lock"
                      alt="Secured"
                    />
                    <small class="text-white align-middle ml-1">
                      Secured Payment
                    </small>
                  </div>

                  <div>
                    <small class="text-white align-middle"> Powered by </small>
                    <img
                      src="@/assets/images/paymongo-green.png"
                      id="paymongo"
                      alt="Secured"
                    />
                  </div>
                </b-col>
              </b-row>
            </b-container>
          </b-row>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { get } from "lodash";

export default {
  name: "CardPayment",
  data() {
    return {
      form: {
        firstName: "",
        lastName: "",
        cardNumber: "",
        expiryDate: "",
        cvv: ""
      },
      billingDetails: null,
      showLoading: false,
      payMongoPaymentIntentInterval: null,
      showCardAuthentication: false
    };
  },
  computed: {
    ...mapState("merchant", ["customerPortalToken"]),
    ...mapState("estore", [
      "orderDetails",
      "errors",
      "payMongoData",
      "loading",
      "payMongoPaymentIntent"
    ]),
    ...mapGetters("estore", ["design", "payMongoPaymentData"]),
    ...mapGetters("customer", ["customerToken"]),
    isWebView() {
      return this.$route.query.token;
    },
    card3dsAuthUrl() {
      return get(this.payMongoPaymentData, "data.redirect.url", null);
    }
  },
  watch: {
    "form.cardNumber"() {
      this.form.cardNumber = this.form.cardNumber.replace(/\D/g, "");
    },
    "form.cvv"() {
      this.form.cvv = this.form.cvv.replace(/\D/g, "");
    }
  },
  filters: {
    currency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "decimal",
        minimumFractionDigits: 2
      }).format(Number.parseFloat(value).toFixed(2));
    }
  },
  methods: {
    numericKeyDown(event) {
      const allowedKeys = ["ArrowLeft", "ArrowRight", "Backspace", "Delete"];

      if (allowedKeys.includes(event.key)) return;
      if (!/\d/.test(event.key)) event.preventDefault();
    },
    postMessage(data) {
      if (window.webkit && window.webkit.messageHandlers.appInterface)
        window.webkit.messageHandlers.appInterface.postMessage(data);

      if (window.appInterface) window.appInterface.postMessage(data);
    },
    async getOrderDetails(id) {
      const request = {
        header: {
          "X-Merchant-Token": this.customerPortalToken
        },
        tracking_uuid: id
      };
      await this.$store.dispatch("estore/getOrderDetails", request);

      if (!this.orderDetails) this.$router.push({ name: "404" });
    },
    formatExpiryDate(event) {
      event.target.value = event.target.value
        .replace(/[^0-9]/g, "")
        .replace(/^([2-9])$/g, "0$1")
        .replace(/^(1{1})([3-9]{1})$/g, "0$1/$2")
        .replace(/^0{1,}/g, "0")
        .replace(/^([0-1]{1}[0-9]{1})([0-9]{1,2}).*/g, "$1/$2");
    },
    validate(key) {
      if (this.errors && key in this.errors) return false;

      return null;
    },
    displayError(key) {
      if (this.errors && key in this.errors) {
        if (
          key === "errors" &&
          this.errors.errors &&
          this.errors.errors.message
        )
          return this.errors.errors.message;

        return Array.isArray(this.errors[key])
          ? this.errors[key].join(" ")
          : this.errors[key];
      } else return "";
    },
    async payViaPayMongo() {
      const expiryDate = this.form.expiryDate.split("/");
      const request = {
        header: {
          Authorization: this.customerToken,
          "X-Merchant-Token": this.customerPortalToken
        },
        payload: {
          card_details: {
            card_number: this.form.cardNumber,
            exp_month: expiryDate[0],
            exp_year: `20${expiryDate[1]}`,
            cvc: this.form.cvv
          },
          billing_details: {
            firstname: this.form.firstName,
            lastname: this.form.lastName,
            address_line_1: this.billingDetails.address1,
            address_line_2: this.billingDetails.address2,
            city: this.billingDetails.city,
            state: this.billingDetails.region,
            postal_code: this.billingDetails.postalCode,
            country: "PH",
            email: this.billingDetails.email,
            phone: this.billingDetails.mobileNumber
          },
          order_transaction_no: this.orderDetails.transaction_no
        }
      };
      await this.$store.dispatch("estore/payViaPayMongo", request);
    },
    async onCompletePayment() {
      const errors = {};
      this.$store.commit("estore/RESET_ERRORS");
      await this.deletePayMongoPaymentMethod();

      for (const field in this.form) {
        if (!this.form[field].trim()) errors[field] = "This field is required.";
      }

      if (!/^(0[1-9]|1[0-2])\/?([0-9]{2})$/.test(this.form.expiryDate))
        errors.expiryDate = "Must be MM/YY format.";

      if (this.form.cvv.length !== 3)
        errors.cvv = "Must be at least 3 characters.";

      if (Object.keys(errors).length) {
        this.$store.commit("estore/SET_ERRORS", errors);

        return;
      }

      await this.payViaPayMongo();

      if (this.payMongoPaymentData) {
        if (this.card3dsAuthUrl) this.showCardAuthentication = true;
        else this.processGetPayMongoIntentInterval();
      }
    },
    processGetPayMongoIntentInterval() {
      this.showLoading = true;

      const _this = this;
      _this.payMongoPaymentIntentInterval = window.setInterval(async () => {
        const request = {
          header: {
            Authorization: _this.customerToken,
            "X-Merchant-Token": _this.customerPortalToken
          },
          id: _this.payMongoPaymentData.intent_id
        };

        await _this.$store.dispatch("estore/getPayMongoPaymentIntent", request);

        if (_this.payMongoPaymentIntent && _this.payMongoPaymentIntent.status) {
          const status = _this.payMongoPaymentIntent.status;

          switch (status) {
            case "succeeded":
              clearInterval(_this.payMongoPaymentIntentInterval);
              _this.showCardAuthentication = false;
              if (_this.isWebView)
                _this.postMessage(_this.payMongoData.message);
              else
                _this.$router.push({
                  name: "PaymentSuccess",
                  query: {
                    orderId: _this.$route.query.orderId,
                    token: _this.customerToken
                  }
                });
              break;
            case "failed":
              clearInterval(_this.payMongoPaymentIntentInterval);
              _this.showCardAuthentication = false;
              _this.$router.push({
                name: "PaymentFailed",
                query: {
                  orderId: _this.$route.query.orderId,
                  token: _this.customerToken
                }
              });
              break;
          }
        }
      }, 2000);
    },
    listenOnPayMongoIntentEvent({ data }) {
      if (data === "3DS-authentication-complete") {
        this.showCardAuthentication = false;
        this.processGetPayMongoIntentInterval();
      }
    },
    async deletePayMongoPaymentMethod() {
      const request = {
        header: {
          Authorization: this.customerToken,
          "X-Merchant-Token": this.customerPortalToken
        },
        transactionNumber: this.orderDetails.transaction_no
      };

      await this.$store.dispatch("estore/deletePayMongoPaymentMethod", request);
    }
  },
  mounted() {
    window.addEventListener("message", this.listenOnPayMongoIntentEvent);
  },
  destroyed() {
    window.removeEventListener("message", this.listenOnPayMongoIntentEvent);
    clearInterval(this.payMongoPaymentIntentInterval);
  },
  created() {
    const {
      orderId,
      token,
      firstName,
      lastName,
      mobileNumber,
      email,
      address1,
      address2,
      city,
      postalCode,
      region,
      country
    } = this.$route.query;

    if (
      !orderId ||
      (!this.customerToken && !token) ||
      !firstName ||
      !lastName ||
      !mobileNumber ||
      !email ||
      !address1 ||
      !address2 ||
      !city ||
      !postalCode ||
      !region ||
      !country
    ) {
      // this.$router.push({ name: "Home" });

      return;
    }

    // if (token) this.$store.commit("auth/SET_TOKEN", token);

    this.billingDetails = {
      firstName,
      lastName,
      mobileNumber,
      email,
      address1,
      address2,
      city,
      postalCode,
      region,
      country
    };

    this.form.firstName = firstName;
    this.form.lastName = lastName;
    this.getOrderDetails(orderId);
  }
};
</script>

<style lang="scss">
@import "@/assets/styles/views/checkout/_card-payment.scss";
</style>
